type LooseRecord = Record<string, any> | null | undefined;

function normalizeKey(value: unknown): string {
  return typeof value === 'string'
    ? value.trim().toLowerCase().replace(/[\s-]+/g, '_')
    : '';
}

function getNestedRoleCandidates(user: LooseRecord): string[] {
  if (!user || typeof user !== 'object') {
    return [];
  }

  const arraySources = [
    Array.isArray(user.clients) ? user.clients : [],
    Array.isArray(user.client_memberships) ? user.client_memberships : [],
    Array.isArray(user.memberships) ? user.memberships : [],
    Array.isArray(user.user_client_memberships) ? user.user_client_memberships : [],
  ];

  return arraySources.flatMap((entries) => entries.flatMap((entry: any) => [
    entry?.role,
    entry?.user_role,
    entry?.module_role,
  ]));
}

export function canSeeAppointmentRescheduleState(user: LooseRecord): boolean {
  if (!user || typeof user !== 'object') {
    return false;
  }

  if (user.is_admin === true) {
    return true;
  }

  const roleCandidates = [
    user.role,
    user.user_role,
    user.module_role,
    user.permissions?.role,
    user.permissions?.user_role,
    ...getNestedRoleCandidates(user),
  ].map(normalizeKey);

  return roleCandidates.some((role) => role === 'root' || role === 'owner' || role === 'admin');
}

export function getWorkItemResolutionType(item: LooseRecord): string {
  if (!item || typeof item !== 'object') {
    return '';
  }

  return normalizeKey(
    item.resolution_type
    ?? item.custom_fields?.resolution_type
    ?? item.custom_fields?.resolution
    ?? item.resolution
    ?? null,
  );
}

/** Parent job id for an appointment (matches Smart Task / completion modal resolution). */
export function resolveParentJobIdFromAppointment(item: LooseRecord): string | null {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const raw =
    (item as any).parent_id
    ?? (item as any).parent_job_id
    ?? item.custom_fields?.parent_job_id;

  if (raw == null || raw === '') {
    return null;
  }

  return String(raw);
}

/**
 * After CompletionModal resolves as partial, rescheduled, or cancelled, the appointment is typically
 * `unresolved` with a matching resolution type — offer creating a new visit on the same parent job.
 */
export function shouldOfferNewFollowUpAppointment(item: LooseRecord): boolean {
  if (normalizeKey(item?.item_type) !== 'appointment') {
    return false;
  }

  if (normalizeKey(item?.status) !== 'unresolved') {
    return false;
  }

  const resolution = getWorkItemResolutionType(item);
  return resolution === 'partial' || resolution === 'rescheduled' || resolution === 'cancelled';
}

export function isAppointmentRescheduleRequested(item: LooseRecord): boolean {
  if (!item || typeof item !== 'object') {
    return false;
  }

  return normalizeKey(item.item_type) === 'appointment'
    && normalizeKey(item.status) === 'unresolved'
    && getWorkItemResolutionType(item) === 'rescheduled';
}

export function isAppointmentPartialResolution(item: LooseRecord): boolean {
  if (!item || typeof item !== 'object') {
    return false;
  }

  return normalizeKey(item.item_type) === 'appointment'
    && normalizeKey(item.status) === 'unresolved'
    && getWorkItemResolutionType(item) === 'partial';
}

export function getAppointmentOperationalStatus(item: LooseRecord): string {
  const normalizedStatus = normalizeKey(item?.status);

  if (isAppointmentRescheduleRequested(item)) {
    return 'closed';
  }

  if (isAppointmentPartialResolution(item)) {
    return 'unresolved';
  }

  return normalizedStatus;
}

export function getAppointmentDisplayStatus(
  item: LooseRecord,
  options?: { privilegedViewer?: boolean },
): string {
  if (isAppointmentPartialResolution(item)) {
    return 'partial';
  }

  if (isAppointmentRescheduleRequested(item)) {
    return options?.privilegedViewer ? 'rescheduled' : 'closed';
  }

  return normalizeKey(item?.status);
}
